'use client';

import { GetActivitiesSortBy, GetActivitiesSortOrder, GetActivitiesStatusFilter } from '@api/models';
import { useTranslations } from 'next-intl';
import { useRouter } from 'next/navigation';
import React from 'react';

import { ActivitiesSection } from '@ui/sections/activities/ActivitiesSection';
import { useActivities } from '@website/hooks/useActivities';
import { useActivitiesFeatured } from '@website/hooks/useActivitiesFeatured';
import { useCurrentPosition } from '@website/hooks/useCurrentPosition';
import { useUserType } from '@website/hooks/useCurrentUser';
import { useHasMounted } from '@website/hooks/useHasMounted';
import { extractCity } from '@website/lib/address';

/**
 * Client component wrapper for ActivitiesSection.
 * Fetches featured activities based on user location (if available)
 * and handles client-side interactions like navigation.
 *
 * Location strategy:
 * 1. IP-based location is fetched immediately (fast)
 * 2. Browser geolocation permission is requested in parallel
 * 3. Activities are shown based on IP location first (no skeleton wait for permission)
 * 4. If user grants geolocation, activities update to precise location (smooth transition)
 * 5. If user denies, IP-based activities remain (no re-fetch, no skeleton)
 * 6. If IP fails, popular activities are shown as fallback
 */

export const ActivitiesSectionClientWrapper: React.FC = () => {
  const userType = useUserType();
  const router = useRouter();
  const t = useTranslations('ActivitiesSection');
  const hasMounted = useHasMounted();

  // Get all translations upfront (Rules of Hooks - must be before any conditional returns)
  const tActivityCard = useTranslations('ActivityCard');
  const atHomeText = tActivityCard('atHome');
  const atVenueText = tActivityCard('atVenue');
  const onlineText = tActivityCard('online');
  const bookingsInYearPrefix = tActivityCard('bookingsInYearPrefix');
  const bookingsInYearMiddle = tActivityCard('bookingsInYearMiddle');
  const bookingsInYearSuffix = tActivityCard('bookingsInYearSuffix');
  const onwardsText = tActivityCard('onwards');
  const freeText = tActivityCard('free');

  const {
    data: position,
    isLoading: isPositionLoading,
    isError: isPositionError,
    error: positionError
  } = useCurrentPosition();

  if (isPositionError && positionError) {
    console.error('Error getting position:', positionError);
  }

  // Fetch featured activities based on location
  // Enabled as soon as we have any position data (IP or browser) or position loading is done
  const shouldFetchFeatured = !isPositionLoading || !!position;

  const {
    data: featuredActivities,
    isLoading: isFeaturedLoading,
    isError: isFeaturedError,
    error: featuredError
  } = useActivitiesFeatured(
    {
      latitude: position?.latitude,
      longitude: position?.longitude,
      limit: 20
    },
    {
      enabled: shouldFetchFeatured,
      // Keep previous data visible during location upgrades (IP → browser)
      // This prevents flickering when switching from IP to precise location
      placeholderData: (previousData) => previousData
    }
  );

  if (isFeaturedError && featuredError) {
    console.error('Error fetching featured activities:', featuredError);
  }

  // Fallback: Fetch popular activities when:
  // 1. No position available (can't fetch featured), OR
  // 2. Featured activities returned empty results (no activities near user)
  const hasFeaturedActivities = featuredActivities && featuredActivities.length > 0;
  const needsPopularFallback = !isPositionLoading && !isFeaturedLoading && !hasFeaturedActivities;

  const {
    data: popularActivitiesResult,
    isLoading: isPopularLoading,
    isError: isPopularError
  } = useActivities(
    {
      statusFilter: GetActivitiesStatusFilter.approved,
      sortBy: GetActivitiesSortBy.bookings,
      sortOrder: GetActivitiesSortOrder.DESC,
      limit: 20
    },
    {
      enabled: needsPopularFallback
    }
  );
  const popularActivities = popularActivitiesResult?.activities;

  const handleActivityClick = (activityId: string) => {
    router.push(`/activity/${activityId}`);
  };

  const handleSeeMoreClick = () => {
    router.push('/search');
  };

  // Determine which activities to show
  const activitiesToShow = hasFeaturedActivities ? featuredActivities : popularActivities;
  const hasActivities = activitiesToShow && activitiesToShow.length > 0;

  // Determine heading based on location availability (only show location-based heading for authenticated users)
  // Use hasMounted to prevent hydration mismatch - server always renders default heading,
  // client updates to location-based heading only after mount
  const isAuthenticated = userType !== 'public';
  const hasLocation = hasMounted && !!position && isAuthenticated;
  const headingText = hasLocation ? t('headingWithLocation') : t('heading');

  // Loading state:
  // - Show loading only during initial IP fetch
  // - Once we have any activities, don't show loading even if upgrading location
  const isLoading =
    (isPositionLoading && !hasActivities) ||
    (isFeaturedLoading && !hasActivities) ||
    (needsPopularFallback && isPopularLoading && !hasActivities);

  // Error state - only if both featured and popular fetches fail
  const isError = isFeaturedError && (isPopularError || !needsPopularFallback);

  // Hide the section for professionals (they're redirected to dashboard) or when no activities
  if (userType === 'professional' || (!isLoading && !hasActivities)) {
    return null;
  }

  return (
    <ActivitiesSection
      activities={(activitiesToShow || []).map((a) => ({
        ...a,
        startingPrice: a.startingPrice ?? 0,
        city: extractCity(a.location?.address)
      }))}
      isLoading={isLoading}
      isError={isError}
      onActivityClick={handleActivityClick}
      onSeeMoreClick={handleSeeMoreClick}
      headingText={headingText}
      seeMoreText={t('seeMore')}
      noActivitiesText={t('noActivities')}
      atHomeText={atHomeText}
      atVenueText={atVenueText}
      onlineText={onlineText}
      bookingsInYearPrefix={bookingsInYearPrefix}
      bookingsInYearMiddle={bookingsInYearMiddle}
      bookingsInYearSuffix={bookingsInYearSuffix}
      onwardsText={onwardsText}
      freeText={freeText}
      skeletonCount={6}
    />
  );
};
